import { useMemo } from 'react';
import styled from 'styled-components';

import { FolderPageNamespace } from 'modules/foldering';
import { useTemplateFolderDetails } from 'modules/campaigns/lib/hooks/useTemplateFolderDetails';
import { ExperimentFlask } from 'ui/icons';
import { SecondaryPageHeader } from 'ui/pageHeader';
import { backgroundColors, spacing } from 'ui/theme';
import { getCampaignEditorLinkWithExperimentTakeover } from 'utils/appRoutes';
import { pxToRem } from 'utils/convert';
import { useProjectLocalStorage } from 'utils/hooks/useProjectLocalStorage';

import { selectIsCampaignAndTemplateEditorsFolderBreadcrumbFlagOn } from 'core/userContext';
import { EditorFolderName } from 'modules/foldering';
import { useSelector } from 'react-redux';
import { useCampaignQueryContext } from '../../context/CampaignQueryContext';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { ContentNameInput } from '../Controls/ContentNameInput';
import { getExperimentAttributeFromCampaign } from '../utils';
import { ExperimentVariantBanner, HeaderContentContainer } from './EditableHeader.styled';
import { PageActions } from './PageActions';
import { SubHeader } from './SubHeader';
import { PAGE_HEADER_VARIANTS } from 'ui/pageHeader/constants';

// hide overflow of the HeaderColLeft (first-child) in order to ellipsize lengthy subheaders
// note: this is special to this implementation:
//       - other implementation may have drop-down menus which should overflow
const StyledSecondaryPageHeader = styled(SecondaryPageHeader)`
  [data-page-header-content] > *:first-child {
    overflow: hidden;
  }
  && {
    @media screen and (max-width: ${pxToRem(893)}) {
      flex-direction: column;
      align-items: start;
    }
  }
  &[data-variant=${PAGE_HEADER_VARIANTS.SECONDARY}] {
    align-items: center;
    background-color: ${backgroundColors.backgroundSecondary};
    padding-top: ${spacing.double};

    @media screen and (max-width: ${pxToRem(893)}) {
      align-items: start; /** prevents header texts being centered when browser size is smaller than 1020px. */
    }

    @media screen and (max-width: ${pxToRem(1680)}) {
      margin-left: ${pxToRem(-40)};
      margin-right: ${pxToRem(-40)};
      padding-left: ${pxToRem(40)};
      padding-right: ${pxToRem(40)};
    }
  }
`;

const HeaderWrapper = styled.div`
  @media screen and (min-width: ${pxToRem(1681)}) {
    width: ${pxToRem(1842)};
    margin: auto;
  }
`;

/**
 *
 * Header component with additional template details information and editable title text
 * Based on designs for TemplateEditor Aurora
 */
export const EditableHeader = ({ isNameInputDisabled = false }) => {
  // Likely will also need to destructure out medium data for other mediums
  const { campaign } = useCampaignQueryContext();
  const { campaignId, experimentFlow: isExperimentFlow } = useQueryParamContext();
  const experimentId = getExperimentAttributeFromCampaign(campaign, 'id') as number;

  const { liveTemplate } = useEditableFieldsContext();

  const isCampaignFolderDetailsQueryEnabled = useSelector(
    selectIsCampaignAndTemplateEditorsFolderBreadcrumbFlagOn,
  );

  /**
   * Retrieve ID of the folder the user came from from local storage so we can return to it when navigating back to the template index.
   * If the user navigated directly to the template editor or from the root folder, the last visited folder ID will be 0.
   */
  const [lastVisitedFolderId] = useProjectLocalStorage(FolderPageNamespace.TEMPLATE_INDEX);

  const { templateFolderDetails } = useTemplateFolderDetails({
    variables: { folderId: lastVisitedFolderId },
    skip: !isCampaignFolderDetailsQueryEnabled || !lastVisitedFolderId,
  });

  const breadcrumbs = useMemo(() => {
    if (isExperimentFlow) {
      return {
        href: getCampaignEditorLinkWithExperimentTakeover(campaignId, experimentId),
        content: 'Back to experiment',
      };
    }
    if (campaignId) {
      return {
        href: '/campaigns/manage',
        content: 'Campaigns',
      };
    } else {
      return {
        href: '/templates',
        content: lastVisitedFolderId ? 'All' : 'See all templates',
      };
    }
  }, [campaignId, experimentId, isExperimentFlow]);

  const lastVisitedFolderName = isCampaignFolderDetailsQueryEnabled
    ? (templateFolderDetails?.name as string)
    : 'Previous Folder';

  return (
    <HeaderWrapper>
      {isExperimentFlow && (
        <ExperimentVariantBanner Icon={<ExperimentFlask height={24} />}>
          {`Editing your variant for ${getExperimentAttributeFromCampaign(campaign, 'name')}`}
        </ExperimentVariantBanner>
      )}
      <StyledSecondaryPageHeader
        documentTitle={`${liveTemplate.name} | Templates`}
        breadcrumb={breadcrumbs}
        data-test="editable-page-header"
        isAngularLink={isExperimentFlow}
        pageSubSection={<SubHeader />}
        pageActions={<PageActions />}
        widthStyle={spacing.PageWidthStyle.normal}
        folderBreadcrumb={
          lastVisitedFolderId
            ? {
                href: `/templates?folderId=${lastVisitedFolderId}`,
                content: (
                  <EditorFolderName
                    name={lastVisitedFolderName}
                    hasIcon={!!templateFolderDetails?.name}
                  />
                ),
              }
            : undefined
        }
      >
        <HeaderContentContainer>
          <ContentNameInput isNameInputDisabled={isNameInputDisabled} />
        </HeaderContentContainer>
      </StyledSecondaryPageHeader>
    </HeaderWrapper>
  );
};
